import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import config from 'constants/config'
import {
  LUXURY_ESCAPES,
  LE_AGENT_HUB,
  LE_AGENT_HUB_CVFR,
} from 'constants/brands'
import productLabels from 'i18n/productLabels'

const BRANDS_WITH_HOTELS_LABEL = [LUXURY_ESCAPES, LE_AGENT_HUB, LE_AGENT_HUB_CVFR]

/**
 * Returns a mapping of SearchVertical to localized labels
 * This hook should be used instead of the deprecated `label` property on SearchVerticalItem
 *
 * Labels are brand-specific where needed (e.g., "Hotels" for luxuryescapes vs "Hotels & Resorts" for other brands)
 */
function useSearchVerticalLabels(): Record<App.SearchVertical, string> {
  const intl = useIntl()

  // Luxury Escapes and Agent Hub brands use "Hotels", other brands use "Hotels & Resorts"
  const useHotelsLabel = BRANDS_WITH_HOTELS_LABEL.includes(config.BRAND)

  return useMemo(() => ({
    hotels: useHotelsLabel
      ? intl.formatMessage(productLabels.hotels)
      : intl.formatMessage(productLabels.hotelsAndResorts),
    flights: intl.formatMessage({
      id: 'sLkLzTD5hY0PE4sluNPZ',
      defaultMessage: 'Flights',
      description: 'Label for flights search vertical in the flight search or booking flow',
    }),
    tours: intl.formatMessage(productLabels.tours),
    cruises: intl.formatMessage({
      id: 'Ekcq_Ca141INx_lXLsXv',
      defaultMessage: 'Cruises',
      description: 'Label for cruises search vertical in the cruise offer or booking section',
    }),
    experiences: intl.formatMessage({
      id: 'ywUiAtcY-X4aKK0vEzrt',
      defaultMessage: 'Experiences',
      description: 'Label for experiences search vertical in the search and discovery interface',
    }),
    'car-hire': intl.formatMessage({
      id: 'anQOxqrCf0twkR8794BA',
      defaultMessage: 'Car Hire',
      description: 'Label for car hire search vertical in the search and discovery interface',
    }),
    cars: intl.formatMessage({
      id: 'kLLnk9h2xbE6X59UZeWS',
      defaultMessage: 'Transfers & Cars',
      description: 'Label for the Transfers & Cars tab in the mobile global search drawer interface',
    }),
    'homes-and-villas': intl.formatMessage({
      id: 'kupvINVGta6Ei7UXjx48',
      defaultMessage: 'Homes & Villas',
      description: 'Label for homes and villas search vertical in the search interface',
    }),
    merchandise: intl.formatMessage({
      id: 'hbd7AUnlZ_0yqB1cgLHU',
      defaultMessage: 'Shop',
      description: 'Label for merchandise/shop search vertical in the search interface',
    }),
  }), [intl, useHotelsLabel])
}

export default useSearchVerticalLabels
